import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import CompanyService from '@services/CompanyService';
import type {
  HistoryResponsibilityLetterProps,
  ResponsibilityLetterProps,
} from '@services/CompanyService/dtos';

import { useAuth } from '@hooks';
import { toastifyApiErrors } from '@utils';
import { ResponsibilityLetterQueryKeys } from '@constants';

export function useResponsibilityLetter() {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedPartnerCompany } = useAuth();

  const partnerCompany = selectedPartnerCompany?.partnerCompany ?? '';

  const isEnabled = Boolean(partnerCompany);

  const companyService = useMemo(() => new CompanyService(), []);

  const responsibilityLetterState = useQuery<ResponsibilityLetterProps>({
    queryKey: [
      ResponsibilityLetterQueryKeys.RESPONSIBILITY_LETTER_STATE,
      partnerCompany,
    ],
    queryFn: () =>
      companyService.responsibilityLetter({
        partnerCompany,
      }),
    enabled: isEnabled,
    onError: (error: unknown) => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const historyResponsibilityLetterState = useQuery<
    HistoryResponsibilityLetterProps[]
  >({
    queryKey: [
      ResponsibilityLetterQueryKeys.HISTORY_RESPONSIBILITY_LETTER_STATE,
      partnerCompany,
    ],
    queryFn: () =>
      companyService.historyResponsibilityLetter({
        partnerCompany,
      }),
    enabled: isEnabled,
    onError: (error: unknown) => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const refreshResponsibilityLetterStates = () => {
    responsibilityLetterState.remove();
    historyResponsibilityLetterState.remove();

    historyResponsibilityLetterState.refetch();
    responsibilityLetterState.refetch();
  };

  return {
    responsibilityLetterState,
    historyResponsibilityLetterState,
    refreshResponsibilityLetterStates,
  };
}
